import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import DeviceService from '../../services/deviceService';

function PreJoinRoom({ meetingTitle, onJoin, onCancel }) {
  const [deviceService] = useState(() => new DeviceService());
  const [devices, setDevices] = useState({
    audioinput: [],
    videoinput: [],
    audiooutput: []
  });
  const [selectedDevices, setSelectedDevices] = useState({
    audioinput: null,
    videoinput: null,
    audiooutput: null
  });
  const [localStream, setLocalStream] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  
  const videoRef = useRef(null);

  useEffect(() => {
    initializeDevices();
    
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStream && videoRef.current) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const initializeDevices = async () => {
    try {
      setLoading(true);
      setError('');

      // First, enumerate devices (will show as empty/generic without permissions)
      await deviceService.enumerateDevices();
      
      // Then request permissions
      const stream = await deviceService.getUserMedia({
        audio: true,
        video: true
      });
      
      setLocalStream(stream);
      setPermissionGranted(true);

      // Re-enumerate devices after getting permissions to get labels
      const devicesWithLabels = await deviceService.enumerateDevices();
      setDevices(devicesWithLabels);
      setSelectedDevices(deviceService.getSelectedDevices());

      // Load saved display name
      const savedName = localStorage.getItem('displayName');
      if (savedName) {
        setDisplayName(savedName);
      }
    } catch (err) {
      console.error('Error initializing devices:', err);
      setError(err.message || 'Failed to access camera and microphone');
      setPermissionGranted(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceChange = async (kind, deviceId) => {
    try {
      deviceService.selectDevice(kind, deviceId);
      setSelectedDevices(deviceService.getSelectedDevices());

      // If changing input devices, restart the stream
      if (kind === 'audioinput' || kind === 'videoinput') {
        if (localStream) {
          localStream.getTracks().forEach(track => track.stop());
        }
        
        const newStream = await deviceService.getUserMedia({
          audio: true,
          video: true
        });
        
        setLocalStream(newStream);
      }
    } catch (err) {
      console.error('Error changing device:', err);
      setError('Failed to switch device');
    }
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleCamera = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  };

  const handleJoin = () => {
    if (!permissionGranted) {
      setError('Please allow camera and microphone access to join');
      return;
    }

    // Save display name for future use
    if (displayName) {
      localStorage.setItem('displayName', displayName);
    }

    onJoin({
      stream: localStream,
      displayName: displayName || 'Anonymous',
      isMuted,
      isCameraOff,
      deviceService
    });
  };

  const handleJoinWithoutMedia = () => {
    onJoin({
      stream: null,
      displayName: displayName || 'Anonymous',
      isMuted: true,
      isCameraOff: true,
      deviceService
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Join Meeting
        </Typography>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {meetingTitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Video Preview */}
            <Box 
              sx={{ 
                position: 'relative',
                width: '100%',
                paddingTop: '56.25%', // 16:9 aspect ratio
                bgcolor: 'black',
                borderRadius: 1,
                mb: 3,
                overflow: 'hidden'
              }}
            >
              {permissionGranted && !isCameraOff ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  <VideocamOffIcon sx={{ fontSize: 64 }} />
                </Box>
              )}

              {/* Preview Controls */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  color={isMuted ? 'error' : 'primary'}
                  onClick={toggleMute}
                  startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
                  disabled={!permissionGranted}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
                <Button
                  variant="contained"
                  color={isCameraOff ? 'error' : 'primary'}
                  onClick={toggleCamera}
                  startIcon={isCameraOff ? <VideocamOffIcon /> : <VideocamIcon />}
                  disabled={!permissionGranted}
                >
                  {isCameraOff ? 'Enable Camera' : 'Disable Camera'}
                </Button>
              </Box>
            </Box>

            {/* Display Name */}
            <TextField
              fullWidth
              label="Your Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="Enter your name"
            />

            {/* Device Settings */}
            {permissionGranted && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon /> Device Settings
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Microphone</InputLabel>
                  <Select
                    value={selectedDevices.audioinput || ''}
                    onChange={(e) => handleDeviceChange('audioinput', e.target.value)}
                    label="Microphone"
                  >
                    {devices.audioinput.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.substring(0, 5)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Camera</InputLabel>
                  <Select
                    value={selectedDevices.videoinput || ''}
                    onChange={(e) => handleDeviceChange('videoinput', e.target.value)}
                    label="Camera"
                  >
                    {devices.videoinput.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${device.deviceId.substring(0, 5)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {devices.audiooutput.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Speaker</InputLabel>
                    <Select
                      value={selectedDevices.audiooutput || ''}
                      onChange={(e) => handleDeviceChange('audiooutput', e.target.value)}
                      label="Speaker"
                    >
                      {devices.audiooutput.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Speaker ${device.deviceId.substring(0, 5)}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {onCancel && (
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button 
                variant="outlined" 
                onClick={handleJoinWithoutMedia}
              >
                Join Without Mic/Cam
              </Button>
              <Button
                variant="contained"
                onClick={handleJoin}
                disabled={!permissionGranted}
                size="large"
              >
                Join Meeting
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default PreJoinRoom;
